export type SubscriptionTier = "free" | "starter" | "plus" | "pro" | "enterprise";
export type SubscriptionStatus = "active" | "canceled" | "canceling" | "past_due" | "paused" | "trialing" | "none";

export const TRIAL_DAYS = 7;
export const ARCHIVE_GRACE_DAYS = 30;

/**
 * Polar product ID environment variable keys, keyed by plan tier.
 * The actual UUIDs are in .env.local / Docker build args — never hardcoded.
 */
export const PLAN_PRODUCT_ENV_KEYS: Record<string, string> = {
  starter: "POLAR_PRODUCT_STARTER_ID",
  plus: "POLAR_PRODUCT_PLUS_ID",
  pro: "POLAR_PRODUCT_PRO_ID",
  basic: "POLAR_PRODUCT_BASIC_ID",
  pro_server: "POLAR_PRODUCT_PRO_SERVER_ID",
  enterprise: "POLAR_PRODUCT_ENTERPRISE_ID",
};

/**
 * Stripe price ID environment variable keys, keyed by plan tier.
 * Used by createStripeCheckoutSession (actions.ts) and the Stripe webhook
 * handler (http.ts). Values live in .env.local / Docker build args — never
 * hardcoded in source.
 *
 * To provision the Stripe products/prices run:
 *   node scripts/seed-stripe-plans.mjs --create
 * Then paste the output into .env.local (and Convex dashboard env vars).
 */
export const STRIPE_PLAN_PRICE_ENV_KEYS: Record<string, string> = {
  starter: "STRIPE_PRICE_STARTER_ID",
  plus: "STRIPE_PRICE_PLUS_ID",
  pro: "STRIPE_PRICE_PRO_ID",
  enterprise: "STRIPE_PRICE_ENTERPRISE_ID",
};

export const PRICING_PLANS = [
  {
    id: "starter",
    name: "Founder Starter",
    description: "Launch your first server community.",
    priceMonthly: 3.99,
    serverLimit: 1,
    badge: null as string | null,
    polarEnvKey: "POLAR_PRODUCT_STARTER_ID",
    stripeEnvKey: "STRIPE_PRICE_STARTER_ID",
    features: [
      "Create 1 organization/community",
      "List your server in Server Finder",
      "Plugin integration & heartbeats",
      "Player account linking",
    ],
  },
  {
    id: "plus",
    name: "Founder Plus",
    description: "Best value for growing networks.",
    priceMonthly: 7.99,
    serverLimit: 5,
    badge: "Most Popular",
    polarEnvKey: "POLAR_PRODUCT_PLUS_ID",
    stripeEnvKey: "STRIPE_PRICE_PLUS_ID",
    features: [
      "Create up to 5 organizations/servers",
      "List your servers in Server Finder",
      "Plugin integration & heartbeats",
      "Player account linking",
    ],
  },
  {
    id: "pro",
    name: "Founder Pro",
    description: "Scale across multiple servers.",
    priceMonthly: 12.99,
    serverLimit: 10,
    badge: null as string | null,
    polarEnvKey: "POLAR_PRODUCT_PRO_ID",
    stripeEnvKey: "STRIPE_PRICE_PRO_ID",
    features: [
      "Create up to 10 organizations/servers",
      "List your servers in Server Finder",
      "Plugin integration & heartbeats",
      "Player account linking",
    ],
  },
] as const;

/**
 * Server Slot Plans — dedicated game server management tiers.
 * These map to Polar products: BlockyNetworks Basic / Pro / Enterprise.
 * Checkout plan IDs: "basic", "pro_server", "enterprise"
 */
export const SERVER_SLOT_PLANS = [
  {
    id: "basic" as const,
    name: "Basic",
    description: "Manage 1 dedicated game server.",
    priceMonthly: 9.99,
    slots: 1,
    badge: null as string | null,
    polarEnvKey: "POLAR_PRODUCT_BASIC_ID",
    specs: { cpu: "1 vCPU", ram: "2 GB RAM", storage: "55 GB SSD", bandwidth: "2 TB" },
    features: [
      "1 managed game server slot",
      "Auto-provisioned Vultr instance",
      "Server Finder listing",
      "Plugin integration & heartbeats",
      "Player account linking",
    ],
  },
  {
    id: "pro_server" as const,
    name: "Pro",
    description: "Run up to 5 game servers.",
    priceMonthly: 24.99,
    slots: 5,
    badge: "Best Value" as string | null,
    polarEnvKey: "POLAR_PRODUCT_PRO_SERVER_ID",
    specs: { cpu: "2 vCPU", ram: "4 GB RAM", storage: "80 GB SSD", bandwidth: "3 TB" },
    features: [
      "Up to 5 managed game server slots",
      "Auto-provisioned Vultr instances",
      "Server Finder listings",
      "Plugin integration & heartbeats",
      "Player account linking",
      "Priority support",
    ],
  },
  {
    id: "enterprise" as const,
    name: "Enterprise",
    description: "Unlimited servers for large networks.",
    priceMonthly: 59.99,
    slots: 999,
    badge: null as string | null,
    polarEnvKey: "POLAR_PRODUCT_ENTERPRISE_ID",
    specs: { cpu: "4 vCPU", ram: "8 GB RAM", storage: "160 GB SSD", bandwidth: "5 TB" },
    features: [
      "Unlimited managed game server slots",
      "Auto-provisioned Vultr instances",
      "Server Finder listings",
      "Plugin integration & heartbeats",
      "Player account linking",
      "Priority support & dedicated SLA",
    ],
  },
] as const;

export type ServerSlotPlanId = (typeof SERVER_SLOT_PLANS)[number]["id"];

export type PricingPlanId = (typeof PRICING_PLANS)[number]["id"];

export const SERVER_LIMITS_BY_TIER: Record<SubscriptionTier, number> = {
  free: 0,
  starter: 1,
  plus: 5,
  pro: 10,
  enterprise: 100,
};

export function getServerLimitForSubscription(
  tier: SubscriptionTier,
  status: SubscriptionStatus
) {
  if (status !== "active" && status !== "trialing") return 0;
  return SERVER_LIMITS_BY_TIER[tier] ?? 0;
}
